import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { FormError } from '../components/ui/FormError.js';
import fieldStyles from '../components/ui/fields.module.css';
import { TextField } from '../components/ui/TextField.js';
import { authClient } from '../lib/auth-client.js';
import { authErrorMessage } from '../lib/auth-errors.js';
import { isInviteReturn, returnToQuery, safeReturnTo } from '../lib/return-to.js';
import { waitForSignedInSession } from '../lib/session.js';

const signInSchema = z.object({
    email: z.email('Enter a valid email address'),
    password: z.string().min(10, 'Use at least 10 characters'),
});

type SignInValues = z.infer<typeof signInSchema>;

export function SignInPage(): ReactElement {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const returnTo = params.get('returnTo');
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<SignInValues>({
        resolver: zodResolver(signInSchema),
        defaultValues: { email: '', password: '' },
    });

    async function onSubmit(values: SignInValues): Promise<void> {
        setServerError(null);
        const result = await authClient.signIn.email({
            email: values.email,
            password: values.password,
        });
        if (result.error) {
            if (result.error.code === 'EMAIL_NOT_VERIFIED') {
                await navigate(
                    `${routePaths.checkEmail}?email=${encodeURIComponent(values.email)}`,
                );
                return;
            }
            setServerError(authErrorMessage(result.error, 'Could not sign in'));
            return;
        }
        // The cookie is set, but the session atom can still hold the logged-out
        // read. Leave only after that atom shows the new session.
        const signedIn = await waitForSignedInSession();
        if (!signedIn) {
            setServerError('Could not sign in');
            return;
        }
        await navigate(safeReturnTo(returnTo));
    }

    return (
        <>
            <h1>{isInviteReturn(returnTo) ? 'Sign in to join this family' : 'Sign in'}</h1>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    void form.handleSubmit(onSubmit)(event);
                }}
            >
                <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    error={form.formState.errors.email?.message}
                    {...form.register('email')}
                />
                <TextField
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    error={form.formState.errors.password?.message}
                    {...form.register('password')}
                />
                <FormError message={serverError} />
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Sign in
                </Button>
            </form>
            <p className={styles.links}>
                <Link to={routePaths.forgotPassword}>Forgot password?</Link>
            </p>
            <p className={styles.links}>
                New here?{' '}
                <Link to={`${routePaths.signUp}${returnToQuery(returnTo)}`}>Create an account</Link>
            </p>
        </>
    );
}
